import { Box, Divider, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import {
    StatusPayoutInterval,
    StatusServiceModule,
    UpdateStatusServiceModuleData,
} from "../../../types/serviceModuleTypes";
import { WalletType } from "../../../types/walletType";

dayjs.extend(utc);

const FALLBACK_OPTION_VALUE = "fallback";

const intervalOptions: { value: StatusPayoutInterval; labelKey: string }[] = [
    { value: "Daily", labelKey: "serviceModulesPage.statusPage.interval.daily" },
    { value: "Weekly", labelKey: "serviceModulesPage.statusPage.interval.weekly" },
    { value: "Monthly", labelKey: "serviceModulesPage.statusPage.interval.monthly" },
];

interface Props {
    module: StatusServiceModule;
    walletTypes: WalletType[];
    canEdit: boolean;
    onSave: (data: UpdateStatusServiceModuleData) => void;
}

export default function StatusModuleSettings(props: Props) {
    const { t } = useTranslation();

    const [statusPointsWalletTypeId, setStatusPointsWalletTypeId] = useState(FALLBACK_OPTION_VALUE);
    const [payoutWalletTypeId, setPayoutWalletTypeId] = useState(FALLBACK_OPTION_VALUE);
    const [payoutInterval, setPayoutInterval] = useState<StatusPayoutInterval>("Monthly");
    const [firstExecution, setFirstExecution] = useState<Dayjs | null>(null);

    useEffect(() => {
        setStatusPointsWalletTypeId(props.module.statusPointsWalletTypeId ?? FALLBACK_OPTION_VALUE);
        setPayoutWalletTypeId(props.module.payoutWalletTypeId ?? FALLBACK_OPTION_VALUE);
        setPayoutInterval(props.module.payoutInterval);
        setFirstExecution(props.module.firstExecution ? dayjs(props.module.firstExecution) : null);
    }, [props.module]);

    const fallbackWalletName = props.walletTypes.find((walletType) => walletType.isFallback)?.name;
    const fallbackOptionLabel = fallbackWalletName
        ? t("serviceModulesPage.statusPage.fallbackWalletOption", { name: fallbackWalletName })
        : t("serviceModulesPage.statusPage.fallbackWalletOptionUnnamed");

    const handleSave = () => {
        props.onSave({
            name: props.module.name,
            iconName: props.module.iconName,
            colorHex: props.module.colorHex,
            sendNotification: props.module.sendNotification,
            isVisible: props.module.isVisible,
            isCompanyWideAssigned: props.module.isCompanyWideAssigned,
            assignedGroupIds: props.module.assignedGroupIds,
            statusPointsWalletTypeId: statusPointsWalletTypeId === FALLBACK_OPTION_VALUE ? null : statusPointsWalletTypeId,
            payoutWalletTypeId: payoutWalletTypeId === FALLBACK_OPTION_VALUE ? null : payoutWalletTypeId,
            payoutInterval,
            firstExecution: firstExecution
                ? dayjs.utc(firstExecution.format("YYYY-MM-DD")).toISOString()
                : null,
        });
    };

    const renderWalletTypeSelect = (
        label: string,
        value: string,
        onChange: (value: string) => void
    ) => (
        <TextField
            select
            fullWidth
            label={label}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={!props.canEdit}
        >
            <MenuItem value={FALLBACK_OPTION_VALUE}>{fallbackOptionLabel}</MenuItem>
            {props.walletTypes
                .filter((walletType) => !walletType.isFallback)
                .map((walletType) => (
                    <MenuItem key={walletType.id} value={walletType.id}>
                        {walletType.name}
                    </MenuItem>
                ))}
        </TextField>
    );

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t("serviceModulesPage.statusPage.settingsTitle")}
            </Typography>

            <Stack sx={{ gap: 3, maxWidth: "900px" }}>
                <Stack direction="row" sx={{ gap: 3 }}>
                    <Stack sx={{ gap: 3, flex: 1 }}>
                        <Typography variant="subtitle1">
                            {t("serviceModulesPage.statusPage.scheduleSectionTitle")}
                        </Typography>

                        <TextField
                            select
                            fullWidth
                            label={t("serviceModulesPage.statusPage.intervalLabel")}
                            value={payoutInterval}
                            onChange={(e) => setPayoutInterval(e.target.value as StatusPayoutInterval)}
                            disabled={!props.canEdit}
                        >
                            {intervalOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {t(option.labelKey)}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Box>
                            <DatePicker
                                label={t("serviceModulesPage.statusPage.firstExecutionLabel")}
                                value={firstExecution}
                                onChange={(newValue) =>
                                    setFirstExecution(dayjs.isDayjs(newValue) ? newValue : null)
                                }
                                format="DD.MM.YYYY"
                                disabled={!props.canEdit}
                                slotProps={{
                                    field: { clearable: true },
                                    textField: { fullWidth: true },
                                }}
                            />
                            {!firstExecution && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                    {t("serviceModulesPage.statusPage.payoutInactiveHint")}
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {t("serviceModulesPage.statusPage.nextExecutionLabel")}:{" "}
                                {props.module.nextExecution
                                    ? dayjs(props.module.nextExecution).format("DD.MM.YYYY")
                                    : "—"}
                            </Typography>
                            {props.module.lastExecution && (
                                <Typography variant="body2" color="text.secondary">
                                    {t("serviceModulesPage.statusPage.lastExecutionLabel")}:{" "}
                                    {dayjs(props.module.lastExecution).format("DD.MM.YYYY")}
                                </Typography>
                            )}
                        </Box>
                    </Stack>

                    <Divider orientation="vertical" flexItem />

                    <Stack sx={{ gap: 3, flex: 1 }}>
                        <Typography variant="subtitle1">
                            {t("serviceModulesPage.statusPage.walletsSectionTitle")}
                        </Typography>

                        {renderWalletTypeSelect(
                            t("serviceModulesPage.statusPage.statusPointsWalletLabel"),
                            statusPointsWalletTypeId,
                            setStatusPointsWalletTypeId
                        )}
                        {renderWalletTypeSelect(
                            t("serviceModulesPage.statusPage.payoutWalletLabel"),
                            payoutWalletTypeId,
                            setPayoutWalletTypeId
                        )}
                    </Stack>
                </Stack>

                {props.canEdit && (
                    <Box>
                        <GeneralButton appVariant="primary" onClick={handleSave}>
                            {t("buttons.save")}
                        </GeneralButton>
                    </Box>
                )}
            </Stack>
        </Box>
    );
}
